// src/components/NotificationConfig/forms/EmailConfigForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faServer,
    faUser,
    faLock,
    faCheck,
    faTimes,
    faExclamationTriangle,
    faEye,
    faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { useNotificationContext } from '../../../contexts/NotificationContext';

const EmailConfigForm = ({ marcaId }) => {
    const { getChannelConfig, updateConfig, loading } = useNotificationContext();
    const [config, setConfig] = useState({
        enabled: false,
        smtpHost: '',
        smtpPort: '',
        smtpUser: '',
        smtpPassword: '',
        fromEmail: '',
        fromName: '',
        useSSL: true,
        useTLS: false
    });
    const [showPassword, setShowPassword] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [isTesting, setIsTesting] = useState(false);

    const loadConfig = useCallback(() => {
        const emailConfig = getChannelConfig('email');
        setConfig({
            enabled: emailConfig.enabled || false,
            smtpHost: emailConfig.smtpHost || '',
            smtpPort: emailConfig.smtpPort || '587',
            smtpUser: emailConfig.smtpUser || '',
            smtpPassword: emailConfig.smtpPassword || '',
            fromEmail: emailConfig.fromEmail || '',
            fromName: emailConfig.fromName || '',
            useSSL: emailConfig.useSSL !== false,
            useTLS: emailConfig.useTLS || false
        });
    }, [getChannelConfig]);

    useEffect(() => {
        if (marcaId) {
            loadConfig();
        }
    }, [marcaId, loadConfig]);

    const handleChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            await updateConfig(marcaId, {
                email: config
            });

            // Mostrar mensaje de éxito
            setTestResult({ success: true, message: 'Configuración guardada correctamente' });
            setTimeout(() => setTestResult(null), 3000);
        } catch (error) {
            setTestResult({ success: false, message: error.message });
        }
    };

    const handleTestConnection = async () => {
        setIsTesting(true);
        setTestResult(null);

        try {
            // Aquí se llamaría al endpoint de prueba del backend
            const response = await fetch('/api/notifications/test-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth-storage-petitprime')}`
                },
                body: JSON.stringify({
                    marcaId,
                    config
                })
            });

            const result = await response.json();

            if (response.ok) {
                setTestResult({ success: true, message: 'Conexión de email probada correctamente' });
            } else {
                setTestResult({ success: false, message: result.message || 'Error probando conexión' });
            }
        } catch (error) {
            setTestResult({ success: false, message: 'Error de conexión' });
        } finally {
            setIsTesting(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center mb-6">
                <FontAwesomeIcon icon={faEnvelope} className="text-blue-500 text-xl mr-3" />
                <h3 className="text-lg font-semibold text-gray-800">Configuración de Email</h3>
                <div className="ml-auto">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={config.enabled}
                            onChange={(e) => handleChange('enabled', e.target.checked)}
                            className="sr-only"
                        />
                        <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.enabled ? 'bg-blue-500' : 'bg-gray-300'
                            }`}>
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                        </div>
                        <span className="ml-2 text-sm text-gray-600">
                            {config.enabled ? 'Habilitado' : 'Deshabilitado'}
                        </span>
                    </label>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Configuración SMTP */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <FontAwesomeIcon icon={faServer} className="mr-2" />
                            Servidor SMTP
                        </label>
                        <input
                            type="text"
                            value={config.smtpHost}
                            onChange={(e) => handleChange('smtpHost', e.target.value)}
                            placeholder="smtp.gmail.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Puerto
                        </label>
                        <input
                            type="number"
                            value={config.smtpPort}
                            onChange={(e) => handleChange('smtpPort', e.target.value)}
                            placeholder="587"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <FontAwesomeIcon icon={faUser} className="mr-2" />
                            Usuario SMTP
                        </label>
                        <input
                            type="email"
                            value={config.smtpUser}
                            onChange={(e) => handleChange('smtpUser', e.target.value)}
                            placeholder="tu-email@gmail.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <FontAwesomeIcon icon={faLock} className="mr-2" />
                            Contraseña SMTP
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={config.smtpPassword}
                                onChange={(e) => handleChange('smtpPassword', e.target.value)}
                                placeholder="Tu contraseña"
                                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={!config.enabled}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                disabled={!config.enabled}
                            >
                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Remitente
                        </label>
                        <input
                            type="email"
                            value={config.fromEmail}
                            onChange={(e) => handleChange('fromEmail', e.target.value)}
                            placeholder="noreply@tudominio.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nombre Remitente
                        </label>
                        <input
                            type="text"
                            value={config.fromName}
                            onChange={(e) => handleChange('fromName', e.target.value)}
                            placeholder="Tu Empresa"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                    </div>
                </div>

                {/* Opciones de seguridad */}
                <div className="flex space-x-4">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={config.useSSL}
                            onChange={(e) => handleChange('useSSL', e.target.checked)}
                            className="mr-2"
                            disabled={!config.enabled}
                        />
                        <span className="text-sm text-gray-700">Usar SSL</span>
                    </label>

                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={config.useTLS}
                            onChange={(e) => handleChange('useTLS', e.target.checked)}
                            className="mr-2"
                            disabled={!config.enabled}
                        />
                        <span className="text-sm text-gray-700">Usar TLS</span>
                    </label>
                </div>

                {/* Mensaje de resultado */}
                {testResult && (
                    <div className={`p-3 rounded-md ${testResult.success
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                        }`}>
                        <div className="flex items-center">
                            <FontAwesomeIcon
                                icon={testResult.success ? faCheck : faTimes}
                                className={`mr-2 ${testResult.success ? 'text-green-600' : 'text-red-600'}`}
                            />
                            {testResult.message}
                        </div>
                    </div>
                )}

                {/* Botones */}
                <div className="flex space-x-3 pt-4">
                    <button
                        type="submit"
                        disabled={loading || !config.enabled}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Guardando...' : 'Guardar Configuración'}
                    </button>

                    <button
                        type="button"
                        onClick={handleTestConnection}
                        disabled={isTesting || !config.enabled}
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isTesting ? 'Probando...' : 'Probar Conexión'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EmailConfigForm; 